import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import confetti from "canvas-confetti";
import { Crown, Trophy, Calendar, ArrowLeft, Sparkles, RefreshCw, Gift, Send, Loader2, CheckCircle2, Send as SendIcon, MailCheck } from "lucide-react";
import { api } from "../lib/config";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const WEEKDAY_ES = {
  Monday: "Lunes", Tuesday: "Martes", Wednesday: "Miércoles",
  Thursday: "Jueves", Friday: "Viernes", Saturday: "Sábado", Sunday: "Domingo",
};

const POSITION_LABELS = ["1°", "2°", "3°", "4°", "5°", "6°", "7°", "8°", "9°", "10°"];

function formatHumanDate(iso) {
  if (!iso) return "";
  try {
    const [y, m, d] = iso.split("-");
    const date = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
    const months = ["enero","febrero","marzo","abril","mayo","junio","julio","agosto","septiembre","octubre","noviembre","diciembre"];
    return `${parseInt(d)} de ${months[date.getMonth()]} de ${y}`;
  } catch { return iso; }
}

function PositionBadge({ position }) {
  const isTop3 = position <= 3;
  const colors = {
    1: { bg: "from-yellow-300 to-yellow-600", ring: "ring-yellow-300/70" },
    2: { bg: "from-slate-200 to-slate-400", ring: "ring-slate-300/70" },
    3: { bg: "from-amber-500 to-amber-800", ring: "ring-amber-400/70" },
  };
  const c = colors[position] || { bg: "from-amber-700 to-amber-900", ring: "ring-amber-500/40" };
  return (
    <div className={`absolute -top-4 left-1/2 -translate-x-1/2 z-10`}>
      <div className={`relative flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br ${c.bg} ring-4 ${c.ring} shadow-2xl`}>
        {isTop3 ? (
          <Crown className="w-7 h-7 text-black/80" strokeWidth={2.5} />
        ) : (
          <span className="text-black font-black text-xl tracking-tight">{POSITION_LABELS[position - 1]}</span>
        )}
      </div>
    </div>
  );
}

function CouponStatusBadge({ winner }) {
  const { coupon_status, coupon_sent_at, coupon_redeemed_at } = winner;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);

  if (!coupon_status && !coupon_sent_at) return null;

  // Helper: time-ago short label (uses `now` state so it re-renders)
  const timeAgo = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const diff = (now - d.getTime()) / 1000;
    if (diff < 60) return "hace 1min";
    if (diff < 3600) return `hace ${Math.floor(diff / 60)}min`;
    if (diff < 86400) return `hace ${Math.floor(diff / 3600)}h`;
    if (diff < 86400 * 7) return `hace ${Math.floor(diff / 86400)}d`;
    return d.toLocaleDateString("es-US", { day: "2-digit", month: "short" });
  };

  if (coupon_status === "redeemed") {
    return (
      <div
        data-testid={`coupon-status-${winner.position}`}
        className="mt-3 flex flex-col items-center"
      >
        <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-500/20 border border-green-400/60 text-green-200 text-[10px] sm:text-xs font-bold uppercase tracking-wider">
          <CheckCircle2 className="w-3 h-3" />
          Canjeado
        </div>
        <div className="text-[10px] text-green-300/70 mt-1">{timeAgo(coupon_redeemed_at)}</div>
      </div>
    );
  }
  if (coupon_status === "expired") {
    return (
      <div data-testid={`coupon-status-${winner.position}`} className="mt-3 flex justify-center">
        <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-zinc-500/20 border border-zinc-500/40 text-zinc-300 text-[10px] sm:text-xs font-bold uppercase tracking-wider">
          Cupón expirado
        </div>
      </div>
    );
  }
  if (coupon_sent_at) {
    return (
      <div data-testid={`coupon-status-${winner.position}`} className="mt-3 flex flex-col items-center">
        <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-pink-500/15 border border-pink-400/50 text-pink-200 text-[10px] sm:text-xs font-bold uppercase tracking-wider">
          <MailCheck className="w-3 h-3" />
          Cupón enviado
        </div>
        <div className="text-[10px] text-pink-300/70 mt-1">{timeAgo(coupon_sent_at)}</div>
      </div>
    );
  }
  return null;
}

function WinnerCard({ winner, index }) {
  const isTop3 = winner.position <= 3;
  const initial = (winner.display_name || "?").charAt(0).toUpperCase();
  return (
    <motion.div
      initial={{ opacity: 0, y: 30, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay: index * 0.12, type: "spring", stiffness: 90, damping: 14 }}
      data-testid={`winner-card-${winner.position}`}
      className="relative"
    >
      <PositionBadge position={winner.position} />
      <div
        className={`relative pt-10 pb-6 px-4 rounded-3xl border ${
          isTop3
            ? "border-yellow-400/70 bg-gradient-to-b from-yellow-950/40 via-black/60 to-black/80 shadow-[0_0_40px_-10px_rgba(250,204,21,0.5)]"
            : "border-yellow-700/30 bg-gradient-to-b from-black/60 to-black/80"
        } backdrop-blur-sm transition-transform hover:scale-[1.02] hover:border-yellow-400/80`}
      >
        {/* Photo / Avatar */}
        <div className="flex justify-center mb-4">
          <div
            className={`relative w-28 h-28 sm:w-32 sm:h-32 rounded-full overflow-hidden ring-4 ${
              isTop3 ? "ring-yellow-400" : "ring-yellow-700/60"
            } shadow-xl`}
          >
            {winner.photo ? (
              <img
                src={winner.photo}
                alt={winner.display_name}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-yellow-600 to-amber-900 text-white text-5xl font-black">
                {initial}
              </div>
            )}
            {isTop3 && (
              <div className="absolute inset-0 pointer-events-none ring-2 ring-yellow-200/40 rounded-full animate-pulse" />
            )}
          </div>
        </div>

        {/* Name */}
        <h3
          className={`text-center font-black tracking-tight ${
            isTop3 ? "text-yellow-200 text-xl sm:text-2xl" : "text-yellow-100 text-lg sm:text-xl"
          } leading-tight`}
        >
          {winner.display_name}
        </h3>

        {/* Camper number */}
        {winner.camper_number_display && (
          <div className="mt-1 text-center font-mono text-yellow-400/90 text-xs sm:text-sm tracking-wider font-bold" data-testid={`winner-camper-num-${winner.position}`}>
            Campista {winner.camper_number_display}
          </div>
        )}

        {/* Time + Sparkle */}
        <div className="mt-2 flex items-center justify-center gap-1.5 text-yellow-500/80 text-xs sm:text-sm">
          <Sparkles className="w-3.5 h-3.5" />
          <span className="font-medium">{winner.joined_time_local || "—"}</span>
        </div>

        {/* Coupon status */}
        <CouponStatusBadge winner={winner} />
      </div>
    </motion.div>
  );
}

export default function Winners() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const dateParam = params.get("date") || "";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const isAdmin = !!(typeof window !== "undefined" && localStorage.getItem("admin_token"));

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/public/winners", {
        params: { limit: 10, ...(dateParam ? { date: dateParam } : {}) },
      });
      // Detect new redemptions (admin only) and notify
      if (isAdmin && data?.winners) {
        const prevRedeemed = new Set(
          data.winners.filter(w => w.coupon_status === "redeemed").map(w => w.coupon_code).filter(Boolean)
        );
        for (const w of (res.data.winners || [])) {
          if (w.coupon_status === "redeemed" && w.coupon_code && !prevRedeemed.has(w.coupon_code)) {
            toast.success(`🎉 ${w.display_name} canjeó su cupón ${w.coupon_code}`, { duration: 8000 });
          }
        }
      }
      setData(res.data);
    } catch (e) { /* */ }
    finally { setLoading(false); }
  };

  const sendCoupons = async () => {
    if (!data?.date) return;
    if (!window.confirm(`¿Generar y enviar cupones de $15 a los ${data.count} primeros inscritos del ${data.date}?`)) return;
    setSending(true);
    try {
      const token = localStorage.getItem("admin_token");
      const res = await api.post("/admin/coupons/send-bulk", null, {
        params: { date: data.date, limit: 10, business: "dulce_vida" },
        headers: { Authorization: `Bearer ${token}` },
      });
      const r = res.data;
      toast.success(`Enviados ${r.sent}/${r.total} cupones`);
      if (r.failed?.length) {
        toast.error(`Fallaron ${r.failed.length}: ${r.failed.map(f => f.name).join(", ")}`, { duration: 10000 });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error enviando cupones");
    } finally {
      setSending(false);
    }
  };

  /* eslint-disable */
  useEffect(() => {
    document.title = "Los 10 Primeros · Operación Rescate";
    load();
  }, [dateParam]);

  // Auto-refresh every 30s when admin is logged in (to see new redemptions live)
  useEffect(() => {
    if (!isAdmin) return;
    const interval = setInterval(() => {
      load();
    }, 30000);
    return () => clearInterval(interval);
  }, [dateParam, isAdmin]);

  // Fire celebration confetti on load
  useEffect(() => {
    if (!data || !data.winners?.length) return;
    const colors = ["#FBBF24", "#F97316", "#FCD34D", "#FFFFFF", "#D4AF37"];
    const fire = () => {
      confetti({ particleCount: 90, spread: 100, origin: { y: 0.55 }, colors });
      confetti({ particleCount: 60, spread: 140, origin: { x: 0.15, y: 0.6 }, colors });
      confetti({ particleCount: 60, spread: 140, origin: { x: 0.85, y: 0.6 }, colors });
    };
    const t1 = setTimeout(fire, 400);
    const t2 = setTimeout(fire, 1400);
    const t3 = setTimeout(fire, 2400);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [data]);
  /* eslint-enable */

  const winners = data?.winners || [];
  const topRow = winners.slice(0, 5);
  const bottomRow = winners.slice(5, 10);

  const dateHuman = useMemo(() => {
    if (!data?.date) return "";
    const weekday = WEEKDAY_ES[data.weekday] || data.weekday;
    return `${weekday}, ${formatHumanDate(data.date)}`;
  }, [data]);

  const handleDateChange = (e) => {
    const v = e.target.value;
    const np = new URLSearchParams(params);
    if (v) np.set("date", v); else np.delete("date");
    setParams(np);
  };

  // Quick Sunday navigation — compute YYYY-MM-DD strings (local TZ)
  const quickSundays = useMemo(() => {
    const today = new Date();
    // JS getDay(): Sunday=0
    const lastSunday = new Date(today);
    lastSunday.setDate(today.getDate() - today.getDay());
    const fmt = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    };
    const minus = (weeks) => {
      const x = new Date(lastSunday);
      x.setDate(lastSunday.getDate() - weeks * 7);
      return fmt(x);
    };
    return [
      { label: "Domingo de hoy", value: fmt(lastSunday) },
      { label: "Domingo pasado", value: minus(1) },
      { label: "Hace 2 semanas", value: minus(2) },
      { label: "Hace 3 semanas", value: minus(3) },
    ];
  }, []);

  const setDate = (v) => {
    const np = new URLSearchParams(params);
    if (v) np.set("date", v); else np.delete("date");
    setParams(np);
  };
  const currentDate = dateParam || data?.date || "";

  return (
    <div className="min-h-screen bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-yellow-950/30 via-black to-black text-white relative overflow-hidden">
      {/* Background grain / sparkles */}
      <div
        className="absolute inset-0 opacity-[0.08] pointer-events-none mix-blend-overlay"
        style={{
          backgroundImage:
            'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' /%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\' /%3E%3C/svg%3E")',
        }}
      />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[80%] h-[300px] bg-yellow-500/10 blur-[120px] pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => nav("/inscritos")}
            data-testid="winners-back-btn"
            className="text-yellow-200/80 hover:text-yellow-200 hover:bg-yellow-500/10"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Inscritos
          </Button>
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={dateParam || (data?.date || "")}
              onChange={handleDateChange}
              data-testid="winners-date-picker"
              className="bg-black/60 border border-yellow-700/50 rounded-lg px-3 py-1.5 text-sm text-yellow-100 focus:outline-none focus:ring-2 focus:ring-yellow-500"
            />
            <Button
              variant="ghost"
              size="icon"
              onClick={load}
              data-testid="winners-refresh-btn"
              className="text-yellow-200/70 hover:text-yellow-200 hover:bg-yellow-500/10"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>

        {/* Quick Sunday chips */}
        <div className="flex flex-wrap items-center justify-center gap-2 mb-4" data-testid="winners-quick-chips">
          {quickSundays.map((q) => {
            const active = currentDate === q.value;
            return (
              <button
                key={q.value}
                onClick={() => setDate(q.value)}
                data-testid={`winners-chip-${q.value}`}
                className={`px-4 py-2 rounded-full text-xs sm:text-sm font-bold uppercase tracking-wider transition-all border ${
                  active
                    ? "bg-yellow-400 text-black border-yellow-300 shadow-[0_0_20px_-4px_rgba(250,204,21,0.6)]"
                    : "bg-black/40 text-yellow-200/80 border-yellow-700/40 hover:border-yellow-400/70 hover:text-yellow-100"
                }`}
              >
                {q.label}
              </button>
            );
          })}
        </div>

        {/* Admin: Send coupons + redemption stats */}
        {isAdmin && data && data.count > 0 && (
          <div className="flex flex-col items-center gap-3 mb-8">
            {/* Stats */}
            {(() => {
              const sent = data.winners.filter(w => w.coupon_sent_at).length;
              const redeemed = data.winners.filter(w => w.coupon_status === "redeemed").length;
              if (sent === 0) return null;
              return (
                <div className="flex items-center gap-3 text-xs sm:text-sm" data-testid="coupon-stats">
                  <span className="px-3 py-1 rounded-full bg-pink-500/15 border border-pink-400/40 text-pink-200 font-bold">
                    📤 {sent} enviados
                  </span>
                  <span className="px-3 py-1 rounded-full bg-green-500/15 border border-green-400/40 text-green-200 font-bold">
                    ✓ {redeemed} canjeados
                  </span>
                  <span className="text-yellow-500/60 text-xs">
                    (auto-actualiza cada 30s)
                  </span>
                </div>
              );
            })()}
            <Button
              onClick={sendCoupons}
              disabled={sending}
              data-testid="admin-send-coupons-btn"
              className="bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white font-bold uppercase tracking-wider shadow-lg"
            >
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              <Gift className="w-4 h-4 mr-2" />
              Enviar cupones a los {data.count}
            </Button>
          </div>
        )}

        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-yellow-400/40 bg-yellow-500/10 text-yellow-300 text-xs sm:text-sm font-bold tracking-widest uppercase mb-4">
            <Trophy className="w-4 h-4" />
            Reconocimiento Especial
          </div>
          <h1 className="font-black tracking-tight leading-[0.95] text-4xl sm:text-6xl lg:text-7xl">
            <span className="bg-gradient-to-b from-yellow-200 via-yellow-400 to-amber-600 bg-clip-text text-transparent drop-shadow-[0_0_30px_rgba(250,204,21,0.4)]">
              Los 10 Primeros
            </span>
          </h1>
          <p className="mt-3 text-yellow-100/70 text-sm sm:text-lg font-medium">
            Que se inscribieron el {dateHuman || "domingo"}
          </p>
          <div className="mt-2 inline-flex items-center gap-1.5 text-yellow-500/60 text-xs sm:text-sm">
            <Calendar className="w-3.5 h-3.5" />
            <span>Operación Rescate · 28 de Agosto</span>
          </div>
        </motion.div>

        {/* Loading / Empty States */}
        {loading && !data && (
          <div className="text-center py-20 text-yellow-300/70">Cargando ganadores...</div>
        )}

        {data && winners.length === 0 && (
          <div className="text-center py-20" data-testid="winners-empty">
            <div className="inline-block p-6 rounded-2xl border border-yellow-700/40 bg-black/40">
              <Trophy className="w-12 h-12 mx-auto text-yellow-600/50 mb-3" />
              <p className="text-yellow-200/80 text-lg font-medium">
                Aún no hay inscritos para este día
              </p>
              <p className="text-yellow-500/50 text-sm mt-1">
                Usa el selector de fecha arriba para elegir otro día
              </p>
            </div>
          </div>
        )}

        {/* Winners Grids */}
        {winners.length > 0 && (
          <>
            {/* TOP ROW (1-5) */}
            <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-6 sm:gap-8 mb-12">
              {topRow.map((w, i) => (
                <WinnerCard key={w.position} winner={w} index={i} />
              ))}
            </div>

            {/* Divider */}
            {bottomRow.length > 0 && (
              <div className="flex items-center gap-4 my-10">
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-yellow-600/40 to-transparent" />
                <div className="flex items-center gap-2 text-yellow-500/70 text-xs uppercase tracking-[0.3em] font-bold">
                  <Sparkles className="w-3.5 h-3.5" />
                  Y también
                </div>
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-yellow-600/40 to-transparent" />
              </div>
            )}

            {/* BOTTOM ROW (6-10) */}
            {bottomRow.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-6 sm:gap-8">
                {bottomRow.map((w, i) => (
                  <WinnerCard key={w.position} winner={w} index={i + 5} />
                ))}
              </div>
            )}

            {/* Footer message */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.5 }}
              className="text-center mt-16 pb-8"
            >
              <p className="text-yellow-100/70 text-base sm:text-lg italic">
                &ldquo;Los primeros serán los primeros en bendición.&rdquo;
              </p>
              <p className="mt-3 text-yellow-300 font-bold text-sm sm:text-base tracking-wide">
                ¡Felicidades guerreros valientes! 🎉
              </p>
            </motion.div>
          </>
        )}
      </div>
    </div>
  );
}
